import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Container,
  VStack,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { requestPasswordReset } from "../api/auth";

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const emailTrim = email.trim();
  const emailError = !emailTrim
    ? "Email is required."
    : !isValidEmail(emailTrim)
      ? "Please enter a valid email address."
      : "";

  async function onSubmit(e) {
    e.preventDefault();
    setApiError("");
    setSuccessMessage("");

    if (emailError) return;

    try {
      setSubmitting(true);

      await requestPasswordReset({ email: emailTrim });

      setSuccessMessage("If that email exists, a reset link has been sent.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to process password reset request.";

      setApiError(typeof msg === "string" ? msg : "Unable to process password reset request.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <style>{`
        .poolBackground {
          background: linear-gradient(120deg,#0a3d62,#1e81b0,#3caea3,#1e81b0,#0a3d62);
          background-size: 400% 400%;
          animation: poolShimmer 8s ease infinite;
        }
        @keyframes poolShimmer {
          0% { background-position: 0% 50%; filter: brightness(100%); }
          50% { background-position: 100% 50%; filter: brightness(120%); }
          100% { background-position: 0% 50%; filter: brightness(100%); }
        }
      `}</style>

      <Box minH="100vh" className="poolBackground" color="white">
        <Box bg="rgba(0, 0, 40, 0.4)" minH="100vh" px={4} py={20}>
          <Container maxW="container.md" centerContent>
            <VStack spacing={8} align="stretch">
              <Box bg="#00007f" px={8} py={6} borderRadius="md" boxShadow="lg" textAlign="center">
                <Heading size={{ base: "xl", md: "2xl" }}>Reset Password</Heading>
              </Box>

              <Box textAlign="center">
                <Text fontSize="lg">Enter your email and we’ll send you a reset link.</Text>
              </Box>

              <Box
                as="form"
                onSubmit={onSubmit}
                bg="rgba(255, 255, 255, 0.15)"
                px={6}
                py={8}
                borderRadius="md"
                boxShadow="xl"
                backdropFilter="blur(6px)"
              >
                <VStack spacing={4}>
                  {apiError ? (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {apiError}
                    </Alert>
                  ) : null}

                  {successMessage ? (
                    <Alert status="success" borderRadius="md" color="#00005f">
                      <AlertIcon />
                      {successMessage}
                    </Alert>
                  ) : null}

                  <FormControl isInvalid={!!emailError && email.length > 0}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      placeholder="Email"
                      type="email"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                    <FormErrorMessage>{emailError}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    bg="#00007f"
                    color="white"
                    w="100%"
                    isLoading={submitting}
                    loadingText="Sending link"
                  >
                    Send Reset Link
                  </Button>

                  <Text fontSize="sm">
                    Remembered your password?{" "}
                    <Link as={RouterLink} to="/login" color="orange.200" fontWeight="600">
                      Back to Login
                    </Link>
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
}